import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from './useAuth'
import styles from './LoginPage.module.css'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function LoginPage() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({})
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const clearFieldError = (field: string) =>
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }))

  const validateForm = (): boolean => {
    const errs: { email?: string; password?: string } = {}
    if (!email.trim()) errs.email = 'El correo es obligatorio.'
    else if (!EMAIL_REGEX.test(email.trim())) errs.email = 'Ingresa un correo válido.'
    if (!password) errs.password = 'La contraseña es obligatoria.'
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!validateForm()) return
    setLoading(true)
    try {
      await login(email, password)
      const redirect = sessionStorage.getItem('fasico:redirect_after_login') ?? '/'
      sessionStorage.removeItem('fasico:redirect_after_login')
      navigate(redirect, { replace: true })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : ''
      if (msg.includes('401') || msg.includes('incorrectos') || msg.includes('Datos inválidos')) {
        setError('Correo o contraseña incorrectos.')
      } else if (msg.includes('conexión') || msg.includes('internet') || msg.includes('Network')) {
        setError('Sin conexión. Verifica tu internet.')
      } else {
        setError('Ocurrió un error. Intenta nuevamente.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGoogle = () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    const redirect = `${window.location.origin}/auth/callback`
    const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirect}&response_type=code&scope=openid%20email%20profile`
    window.location.href = url
  }

  return (
    <div className={styles.page}>
      {/* Left panel — brand */}
      <div className={styles.brand}>
        <div className={styles.brandInner}>
          <div className={styles.brandLogo}>
            <img src="/icono-app-ui.svg" alt="Fásico" className={styles.brandIsotipo} />
            <span className={styles.brandName}>Fásico</span>
          </div>
          <p className={styles.brandTagline}>Documentación TE1 para electricistas SEC</p>
          <div className={styles.brandFeatures}>
            <div className={styles.feature}>
              <span className={styles.featureIcon}>⚡</span>
              <span>13 documentos generados automáticamente</span>
            </div>
            <div className={styles.feature}>
              <span className={styles.featureIcon}>📋</span>
              <span>Conforme a DS 08/2019 RIC</span>
            </div>
            <div className={styles.feature}>
              <span className={styles.featureIcon}>📱</span>
              <span>Funciona en tu celular Android</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className={styles.formPanel}>
        <div className={styles.formInner}>
          {/* Logo visible solo en mobile */}
          <div className={styles.mobileHeader}>
            <img src="/isotipo.svg" alt="" aria-hidden="true" className={styles.mobileIsotipo} />
            <span className={styles.mobileBrand}>Fásico</span>
          </div>

          <h1 className={styles.title}>Iniciar sesión</h1>
          <p className={styles.subtitle}>Ingresa tus datos para continuar</p>

          {error && (
            <div className={styles.errorBox} role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className={styles.form} noValidate>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="email">Correo electrónico</label>
              <input
                id="email"
                type="email"
                className={styles.input}
                value={email}
                onChange={(e) => { setEmail(e.target.value); clearFieldError('email') }}
                placeholder="tu@email.com"
                autoComplete="email"
                required
              />
              {fieldErrors.email && <span className={styles.fieldError}>{fieldErrors.email}</span>}
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="password">Contraseña</label>
              <input
                id="password"
                type="password"
                className={styles.input}
                value={password}
                onChange={(e) => { setPassword(e.target.value); clearFieldError('password') }}
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
              {fieldErrors.password && <span className={styles.fieldError}>{fieldErrors.password}</span>}
            </div>

            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={loading}
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
            </button>
          </form>

          <div className={styles.divider}>
            <span className={styles.dividerLine} />
            <span className={styles.dividerText}>o</span>
            <span className={styles.dividerLine} />
          </div>

          <button
            type="button"
            className={styles.btnGoogle}
            onClick={handleGoogle}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
            </svg>
            Continuar con Google
          </button>

          <p className={styles.register}>
            ¿No tienes cuenta?{' '}
            <Link to="/register" className={styles.registerLink}>Regístrate gratis</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
